import React from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ProductsContainer } from '@fuww/library/src/CompanyProfile';
import Head from '../components/Head';
import withQuery from '../lib/withQuery';
import withApollo from '../lib/apollo';
import profileBySlugQuery
  from '../lib/queries/products/profileBySlugQuery.graphql';
import messages from '../lib/messages.json';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';
import Layout from '../components/FashionFinder/Profile/Layout';
import DynamicProducts from '../components/Products/DynamicProducts';
import throw404 from '../lib/throw404';
import {
  companyProfileCollectionImageConfigurations,
  companyProfileImageConfigurations,
  companyProfileLogoImageConfigurations,
  companyProfileMobileImageConfigurations,
} from '../lib/imageConfigurations';

const marketId = 8;

const FashionFinderProfileProducts = ({
  data: { profileBySlug },
  route: productRoute,
}) => {
  const intl = useIntl();

  if (!profileBySlug) {
    return throw404();
  }

  const {
    id: profileId,
    brands,
    page: {
      title: brandName,
    },
    type,
  } = profileBySlug;

  if (type !== 'COMPANY') {
    return throw404();
  }

  return (
    <>
      <Head
        description={intl.formatMessage(
          messages['fashion-finder-profile.description'],
          { brandName },
        )}
        title={intl.formatMessage(
          messages['fashion-finder-profile.title'],
          { brandName },
        )}
        subjectType="Profile"
        subjectId={profileId}
        payload={{ marketId }}
      />
      <Layout
        profile={profileBySlug}
      >
        <ProductsContainer>
          <DynamicProducts
            brandIdsScope={brands.map(({ id }) => id)}
            route={productRoute}
            marketId={marketId}
            includeProfile
          />
        </ProductsContainer>
      </Layout>
    </>
  );
};

FashionFinderProfileProducts.propTypes = {
  data: PropTypes.shape({
    profileBySlug: PropTypes.shape({
      id: PropTypes.string,
      page: PropTypes.shape({
        title: PropTypes.string,
      }),
      type: PropTypes.string,
      brands: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  route: PropTypes.string,
};

FashionFinderProfileProducts.defaultProps = {
  data: {},
  route: null,
};

const FashionFinderProfileProductsWithQuery = withQuery(
  FashionFinderProfileProducts,
);

const FashionFinderProfile = () => {
  const { query: { slug } } = useRouter();

  return (
    <FashionFinderProfileProductsWithQuery
      query={profileBySlugQuery}
      variables={{
        slug,
        imageConfigurations: companyProfileImageConfigurations,
        mobileImageConfigurations: companyProfileMobileImageConfigurations,
        logoConfigurations: companyProfileLogoImageConfigurations,
        collectionImageConfigurations:
        companyProfileCollectionImageConfigurations,
      }}
      errorMessage="Error loading this Fashion Finder profile"
      route="fashion-finder-product"
    />
  );
};

FashionFinderProfile.getLayout = (page) => page;

FashionFinderProfile.getInitialProps = redirectRouteToDomain(
  'fashion-finder-profile',
);

export default withApollo(FashionFinderProfile);
